import type {
  WhatsAppCampaign,
  WhatsAppCampaignRuleConfig,
  WhatsAppCampaignSendPolicy,
  WhatsAppCampaignType,
  WhatsAppMessageCategory,
  WhatsAppSendMode,
  WhatsAppServiceVertical,
  WhatsAppTargetAudience,
} from '../../models/MasterAdmin/whatsappCampaign';
import type { WhatsAppTemplateVariableMapping } from '../../models/MasterAdmin/whatsappHub';
import { deduplicateDoctorPrefix } from './whatsappDoctorHonorific';

export const inputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900';

export type CampaignFormState = {
  name: string;
  campaignType: WhatsAppCampaignType;
  sendMode: WhatsAppSendMode;
  analysisSource: 'MANUAL' | 'AI_BACKEND' | 'RULES';
  serviceVertical: WhatsAppServiceVertical | '';
  templateName: string;
  templateLanguageCode: string;
  bodyValues: string[];
  templateVariableMappings: WhatsAppTemplateVariableMapping[];
  headerMediaUrl: string;
  headerMimeType: string;
  headerFileName: string;
  messageCategory: WhatsAppMessageCategory;
  targetAudience: WhatsAppTargetAudience;
  recipientMode: 'audience' | 'custom';
  targetUserId: string;
  targetVendorId: string;
  targetDisplayName: string;
  targetPhone: string;
  customPhonesText: string;
  filterCity: string;
  filterState: string;
  scheduleMode: 'now' | 'later';
  scheduledAt: string;
  ruleConfig: WhatsAppCampaignRuleConfig;
  analysisConfigJson: string;
  sendPolicy: WhatsAppCampaignSendPolicy;
  retryTemplatePlan: string[];
  retryHeaderMediaPlan: Array<{
    headerMediaUrl: string;
    headerMimeType: string;
    headerFileName: string;
  }>;
  applyRetryHeaderToAll: boolean;
};

export const defaultRuleConfig = (): WhatsAppCampaignRuleConfig => ({
  minDwellTimeSec: 3,
  activeDwellTimeSec: 7,
  minIntentScore: 60,
  delayMinutes: 15,
  conversionCheckMinutes: 30,
  cooldownHours: 24,
  intentEvents: [],
  scoreRules: {
    activeReadingPoints: 25,
    highIntentEventPoints: 35,
    repeatViewPoints: 30,
    repeatWindowMinutes: 60,
  },
  userFilters: { cities: [], platforms: [] },
});

export const defaultSendPolicy = (): WhatsAppCampaignSendPolicy => ({
  enabled: true,
  phaseSize: 250,
  sendWindowHours: 24,
  weeklyCapDays: 7,
  nonReaderExcludeDays: 30,
  excludeNonReaders: true,
  enforceWeeklyCap: true,
  includeOptOut: true,
});

export function emptyRetryHeaderMediaSlot() {
  return { headerMediaUrl: '', headerMimeType: '', headerFileName: '' };
}

export function normalizeRetryHeaderMediaPlan(
  raw: WhatsAppCampaign['retryHeaderMediaPlan'] | CampaignFormState['retryHeaderMediaPlan'] | null | undefined
): CampaignFormState['retryHeaderMediaPlan'] {
  const arr = Array.isArray(raw) ? raw : [];
  return Array.from({ length: 7 }, (_, i) => {
    const item = arr[i];
    if (!item) return emptyRetryHeaderMediaSlot();
    if (typeof item === 'string') {
      return { headerMediaUrl: item, headerMimeType: '', headerFileName: '' };
    }
    return {
      headerMediaUrl: String(item.headerMediaUrl || '').trim(),
      headerMimeType: String(item.headerMimeType || '').trim(),
      headerFileName: String(item.headerFileName || '').trim(),
    };
  });
}

export const emptyCampaignForm = (): CampaignFormState => ({
  name: '',
  campaignType: 'BROADCAST',
  sendMode: 'MANUAL',
  analysisSource: 'RULES',
  serviceVertical: 'doctor_consultation',
  templateName: '',
  templateLanguageCode: 'en',
  bodyValues: [],
  templateVariableMappings: [],
  headerMediaUrl: '',
  headerMimeType: '',
  headerFileName: '',
  messageCategory: 'MARKETING',
  targetAudience: 'ALL_USERS',
  recipientMode: 'audience',
  targetUserId: '',
  targetVendorId: '',
  targetDisplayName: '',
  targetPhone: '',
  customPhonesText: '',
  filterCity: '',
  filterState: '',
  scheduleMode: 'now',
  scheduledAt: '',
  ruleConfig: defaultRuleConfig(),
  analysisConfigJson: '{}',
  sendPolicy: defaultSendPolicy(),
  retryTemplatePlan: [],
  retryHeaderMediaPlan: Array.from({ length: 7 }, () => emptyRetryHeaderMediaSlot()),
  applyRetryHeaderToAll: true,
});

export function isSingleAudience(audience: WhatsAppTargetAudience | string): boolean {
  return audience === 'SINGLE_USER' || audience === 'SINGLE_VENDOR' || audience === 'SINGLE_DOCTOR';
}

export function isPickerAudience(audience: WhatsAppTargetAudience | string): boolean {
  return isSingleAudience(audience) || audience === 'CUSTOM_PHONES';
}

export function recipientsToPhoneLines(
  recipients: Array<{ countryCode?: string; phoneNumber?: string }>
): string {
  return recipients
    .map((r) => {
      const cc = String(r.countryCode || '+91').replace(/\s/g, '');
      const pn = String(r.phoneNumber || '').replace(/\D/g, '').slice(-10);
      if (!pn) return '';
      return `${cc.startsWith('+') ? cc : `+${cc}`}${pn}`;
    })
    .filter(Boolean)
    .join('\n');
}

export function campaignToForm(c: WhatsAppCampaign): CampaignFormState {
  return {
    name: c.name,
    campaignType: c.campaignType || 'BROADCAST',
    sendMode: c.sendMode || 'MANUAL',
    analysisSource: (c.analysisSource as CampaignFormState['analysisSource']) || 'AI_BACKEND',
    serviceVertical: (c.serviceVertical as WhatsAppServiceVertical) || 'doctor_consultation',
    templateName: c.templateName,
    templateLanguageCode: c.templateLanguageCode || 'en',
    bodyValues: Array.isArray(c.bodyValues) ? c.bodyValues : [],
    templateVariableMappings: c.templateVariableMappings || [],
    headerMediaUrl: c.headerMediaUrl || '',
    headerMimeType: c.headerMimeType || '',
    headerFileName: c.headerFileName || '',
    messageCategory: c.messageCategory,
    targetAudience: c.targetAudience,
    recipientMode: c.recipientMode || 'audience',
    targetUserId: c.targetUserId || '',
    targetVendorId: c.targetVendorId || '',
    targetDisplayName: '',
    targetPhone: '',
    customPhonesText: (c.customPhones || []).join('\n'),
    filterCity: c.filterCity || '',
    filterState: c.filterState || '',
    scheduleMode: 'now',
    scheduledAt: '',
    ruleConfig: { ...defaultRuleConfig(), ...(c.ruleConfig || {}) },
    analysisConfigJson: c.analysisConfig ? JSON.stringify(c.analysisConfig, null, 2) : '{}',
    sendPolicy: { ...defaultSendPolicy(), ...(c.sendPolicy || {}) },
    retryTemplatePlan: Array.isArray(c.retryTemplatePlan) ? [...c.retryTemplatePlan] : [],
    retryHeaderMediaPlan: normalizeRetryHeaderMediaPlan(
      c.retryHeaderMediaPlan || c.sendPolicy?.retryHeaderMediaPlan
    ),
    applyRetryHeaderToAll: !normalizeRetryHeaderMediaPlan(
      c.retryHeaderMediaPlan || c.sendPolicy?.retryHeaderMediaPlan
    ).some((p) => Boolean(p.headerMediaUrl)),
  };
}

/** Preview body values from mappings (or fall back to static bodyValues). */
export function previewBodyValuesFromMappings(
  mappings: WhatsAppTemplateVariableMapping[],
  bodyValues: string[],
  variableCount: number
): string[] {
  const count = Math.max(variableCount, mappings.length, bodyValues.length);
  if (count <= 0) return [];
  return Array.from({ length: count }, (_, i) => {
    const mapping = mappings.find((m) => m.slot === i + 1);
    if (mapping) {
      if (mapping.sourceType === 'static') return deduplicateDoctorPrefix(mapping.staticValue || bodyValues[i] || '');
      if (mapping.sourceType === 'deep_link') return mapping.label || '[Deep link]';
      if (mapping.sourceType === 'context') {
        return deduplicateDoctorPrefix(mapping.label || mapping.contextKey || bodyValues[i] || `[{{${i + 1}}}]`);
      }
    }
    const raw = bodyValues[i];
    if (!raw || raw === '@auto') return deduplicateDoctorPrefix(mapping?.label || `Sample ${i + 1}`);
    return deduplicateDoctorPrefix(raw);
  });
}

export function bodyValuesFromMappings(
  mappings: WhatsAppTemplateVariableMapping[],
  campaignType: WhatsAppCampaignType
): string[] {
  if (!mappings.length) return [];
  return mappings
    .slice()
    .sort((a, b) => a.slot - b.slot)
    .map((m) => {
      if (m.sourceType === 'static') return m.staticValue || '';
      return campaignType === 'AUTOMATED' ? '@auto' : `@${m.contextKey || m.sourceType}`;
    });
}

export function buildCampaignPayload(
  form: CampaignFormState,
  serviceEvents: string[]
): Partial<WhatsAppCampaign> {
  let analysisConfig: Record<string, unknown> | null = null;
  try {
    analysisConfig = form.analysisConfigJson.trim() ? JSON.parse(form.analysisConfigJson) : null;
  } catch {
    analysisConfig = null;
  }

  const customPhones = form.customPhonesText
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const targetAudience = form.targetAudience;
  const isSingle = isSingleAudience(targetAudience);

  return {
    name: form.name.trim(),
    campaignType: form.campaignType,
    sendMode: form.sendMode,
    analysisSource: form.analysisSource,
    serviceVertical: form.serviceVertical || null,
    templateName: form.templateName,
    templateLanguageCode: form.templateLanguageCode,
    bodyValues: form.bodyValues,
    templateVariableMappings: form.templateVariableMappings,
    headerMediaUrl: form.headerMediaUrl.trim() || null,
    headerMimeType: form.headerMimeType.trim() || null,
    headerFileName: form.headerFileName.trim() || null,
    messageCategory: form.messageCategory,
    targetAudience,
    recipientMode: targetAudience === 'CUSTOM_PHONES' ? 'custom' : form.recipientMode || 'audience',
    targetUserId:
      targetAudience === 'SINGLE_USER' ? form.targetUserId.trim() || null : null,
    targetVendorId:
      targetAudience === 'SINGLE_VENDOR' || targetAudience === 'SINGLE_DOCTOR'
        ? form.targetVendorId.trim() || null
        : null,
    customPhones: targetAudience === 'CUSTOM_PHONES' ? customPhones : [],
    filterCity: !isSingle && targetAudience !== 'CUSTOM_PHONES' ? form.filterCity.trim() || null : null,
    filterState: !isSingle && targetAudience !== 'CUSTOM_PHONES' ? form.filterState.trim() || null : null,
    ruleConfig: {
      ...form.ruleConfig,
      intentEvents:
        form.ruleConfig.intentEvents?.length ? form.ruleConfig.intentEvents : serviceEvents,
    },
    analysisConfig,
    sendPolicy: form.messageCategory === 'MARKETING' ? form.sendPolicy : { ...form.sendPolicy, enabled: false },
    retryTemplatePlan: form.retryTemplatePlan.filter(Boolean),
    retryHeaderMediaPlan: form.applyRetryHeaderToAll
      ? null
      : form.retryHeaderMediaPlan.map((slot) =>
          slot.headerMediaUrl.trim()
            ? {
                headerMediaUrl: slot.headerMediaUrl.trim(),
                headerMimeType: slot.headerMimeType.trim() || null,
                headerFileName: slot.headerFileName.trim() || null,
              }
            : null
        ),
  };
}

export function validateCampaignForm(form: CampaignFormState): string | null {
  if (!form.name.trim()) return 'Campaign name is required';
  if (!form.templateName.trim()) return 'Select an approved template';
  if (form.campaignType !== 'BROADCAST') return null;

  if (form.targetAudience === 'SINGLE_USER' && !form.targetUserId.trim()) {
    return 'Select a user (or contact linked to a user) for Single user audience';
  }
  if (
    (form.targetAudience === 'SINGLE_VENDOR' || form.targetAudience === 'SINGLE_DOCTOR') &&
    !form.targetVendorId.trim()
  ) {
    return `Select a ${form.targetAudience === 'SINGLE_DOCTOR' ? 'doctor' : 'vendor'} for this audience`;
  }
  if (form.targetAudience === 'CUSTOM_PHONES') {
    const phones = form.customPhonesText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (!phones.length) return 'Add at least one phone number for Custom phones audience';
  }
  return null;
}
